/**
 * MonteCarloWizard — 5-step wizard to create + run a Monte Carlo simulation.
 * Phase 4.10 β6 — WQ-241.
 *
 * Steps:
 *   1. Setup        — name (required) + description (optional)
 *   2. Variables    — 1+ rows: name + distribution type + dynamic per-type params
 *   3. Iterations   — num_trials (100–100,000) + RNG seed (optional)
 *   4. Output       — target_expression (Python expression over variables)
 *   5. Review       — summary + Launch (creates then runs)
 *
 * Wraps existing POST /api/admin/ai/analytics/simulations + .../run.
 *
 * Pattern reuse: copy-paste-with-divergence per WQ-237 NOTES. 4th β-track wizard.
 * Chrome copied from WQ-240 DataQualityLauncher (closest template). DO NOT
 * pre-extract WizardModalShell — that is WQ-251 (Owen-approved 2026-04-27).
 *
 * Step 2 distribution params (per-type dynamic) is the most divergent step
 * body in the entire wizard family — appropriate divergence; WQ-251 leaves
 * step bodies untouched.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  createSimulation,
  runSimulation,
  type DistributionType,
  type DistributionDef,
  type SimulationOut,
} from '../../services/monteCarloService';
import WizardModalShell from '../wizard/WizardModalShell';

type WizardStep = 1 | 2 | 3 | 4 | 5;

interface MonteCarloWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (simulationId: string) => void;
}

interface VariableRow {
  id: number;
  name: string;
  distribution: DistributionType;
  params: Record<string, string>;  // string for input control; parsed at submit
}

const STEP_TITLES: Record<WizardStep, string> = {
  1: 'Simulation setup',
  2: 'Configure input variables',
  3: 'Iteration parameters',
  4: 'Define output expression',
  5: 'Review & launch',
};

interface ParamFieldSpec {
  name: string;
  label: string;
  hint?: string;
  validate?: (v: number) => boolean;
}

const PARAM_SCHEMAS: Record<DistributionType, ParamFieldSpec[]> = {
  uniform: [
    { name: 'min', label: 'Min' },
    { name: 'max', label: 'Max' },
  ],
  normal: [
    { name: 'mean', label: 'Mean' },
    { name: 'stddev', label: 'Std Dev', validate: (v) => v > 0 },
  ],
  triangular: [
    { name: 'min', label: 'Min' },
    { name: 'mode', label: 'Mode' },
    { name: 'max', label: 'Max' },
  ],
  lognormal: [
    { name: 'mu', label: 'μ' },
    { name: 'sigma', label: 'σ', validate: (v) => v > 0 },
  ],
  beta: [
    { name: 'alpha', label: 'α', validate: (v) => v > 0 },
    { name: 'beta', label: 'β', validate: (v) => v > 0 },
  ],
  fixed: [
    { name: 'value', label: 'Value' },
  ],
};

const ALL_DISTRIBUTIONS: DistributionType[] = ['uniform', 'normal', 'triangular', 'lognormal', 'beta', 'fixed'];

function makeEmptyRow(id: number): VariableRow {
  return { id, name: '', distribution: 'uniform', params: { min: '0', max: '1' } };
}

function parseRowParams(row: VariableRow): Record<string, number> | null {
  const schema = PARAM_SCHEMAS[row.distribution];
  const out: Record<string, number> = {};
  for (const field of schema) {
    const raw = row.params[field.name];
    if (raw === undefined || raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    if (field.validate && !field.validate(n)) return null;
    out[field.name] = n;
  }
  // Cross-field validation
  if (row.distribution === 'uniform' && out.max <= out.min) return null;
  if (row.distribution === 'triangular' && (out.min > out.mode || out.mode > out.max || out.min >= out.max)) return null;
  return out;
}

function isVariableValid(row: VariableRow): boolean {
  if (row.name.trim().length === 0) return false;
  return parseRowParams(row) !== null;
}

const MonteCarloWizard: React.FC<MonteCarloWizardProps> = ({ isOpen, onClose, onSubmitted }) => {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  // Step 2
  const [variables, setVariables] = useState<VariableRow[]>([makeEmptyRow(0)]);
  const [nextRowId, setNextRowId] = useState<number>(1);

  // Step 3
  const [numTrials, setNumTrials] = useState<number>(10000);
  const [seedRaw, setSeedRaw] = useState<string>('');

  // Step 4
  const [targetExpression, setTargetExpression] = useState<string>('');

  // Submit
  const [launching, setLaunching] = useState<boolean>(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setName('');
      setDescription('');
      setVariables([makeEmptyRow(0)]);
      setNextRowId(1);
      setNumTrials(10000);
      setSeedRaw('');
      setTargetExpression('');
      setLaunchError(null);
    }
  }, [isOpen]);

  const variableNames = useMemo(
    () => variables.map((v) => v.name.trim()).filter((n) => n.length > 0),
    [variables]
  );

  const allVariablesValid = useMemo(
    () => variables.length > 0 && variables.every(isVariableValid),
    [variables]
  );

  const variableNamesUnique = useMemo(
    () => new Set(variableNames).size === variableNames.length,
    [variableNames]
  );

  const seedValid = useMemo(() => {
    if (seedRaw.trim() === '') return true;
    const n = Number(seedRaw);
    return Number.isInteger(n);
  }, [seedRaw]);

  const canAdvance = useMemo(() => {
    if (step === 1) return name.trim().length > 0;
    if (step === 2) return allVariablesValid && variableNamesUnique;
    if (step === 3) return numTrials >= 100 && numTrials <= 100000 && seedValid;
    if (step === 4) return targetExpression.trim().length > 0;
    return false;
  }, [step, name, allVariablesValid, variableNamesUnique, numTrials, seedValid, targetExpression]);

  const handleNext = () => {
    if (!canAdvance) return;
    setStep((prev) => (prev < 5 ? ((prev + 1) as WizardStep) : prev));
  };

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? ((prev - 1) as WizardStep) : prev));
  };

  const addVariable = () => {
    setVariables((prev) => [...prev, makeEmptyRow(nextRowId)]);
    setNextRowId((id) => id + 1);
  };

  const removeVariable = (id: number) => {
    setVariables((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  };

  const updateVariableField = (id: number, patch: Partial<VariableRow>) => {
    setVariables((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const updateVariableDistribution = (id: number, distribution: DistributionType) => {
    setVariables((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const schema = PARAM_SCHEMAS[distribution];
        const params: Record<string, string> = {};
        for (const f of schema) {
          // Sensible defaults per field
          if (f.name === 'min') params[f.name] = '0';
          else if (f.name === 'max') params[f.name] = '1';
          else if (f.name === 'mean') params[f.name] = '0';
          else if (f.name === 'stddev' || f.name === 'sigma') params[f.name] = '1';
          else if (f.name === 'mode') params[f.name] = '0.5';
          else if (f.name === 'mu') params[f.name] = '0';
          else if (f.name === 'alpha' || f.name === 'beta') params[f.name] = '2';
          else if (f.name === 'value') params[f.name] = '0';
          else params[f.name] = '';
        }
        return { ...r, distribution, params };
      })
    );
  };

  const updateVariableParam = (id: number, field: string, value: string) => {
    setVariables((prev) =>
      prev.map((r) => (r.id === id ? { ...r, params: { ...r.params, [field]: value } } : r))
    );
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setLaunchError(null);
    try {
      const inputDistributions: Record<string, DistributionDef> = {};
      for (const row of variables) {
        const parsed = parseRowParams(row);
        if (!parsed) throw new Error(`Invalid parameters for variable ${row.name}`);
        inputDistributions[row.name.trim()] = { type: row.distribution, params: parsed };
      }
      const created: SimulationOut = await createSimulation(authFetch, {
        name: name.trim(),
        description: description.trim() || undefined,
        input_distributions: inputDistributions,
        target_expression: targetExpression.trim(),
        num_trials: numTrials,
        random_seed: seedRaw.trim() === '' ? undefined : Number(seedRaw),
      });
      await runSimulation(authFetch, created.id);
      if (onSubmitted) onSubmitted(created.id);
      onClose();
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'Failed to launch simulation');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <WizardModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Run Monte Carlo Simulation"
      totalSteps={5}
      currentStep={step}
      stepTitle={STEP_TITLES[step]}
      testIdPrefix="mc-wizard"
      canAdvance={canAdvance}
      submitting={launching}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleLaunch}
      launchError={launchError}
    >
      <>
          {step === 1 && (
            <div data-testid="mc-wizard-step-1">
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Simulation name *
                </label>
                <input
                  type="text"
                  data-testid="mc-wizard-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Q3 Revenue Forecast"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Description (optional)
                </label>
                <textarea
                  data-testid="mc-wizard-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="What scenario does this simulation explore?"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="mc-wizard-step-2">
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Define one or more random input variables. Each variable's name is referenced from the target expression in Step 4.
              </div>
              <div data-testid="mc-wizard-variable-list">
                {variables.map((row) => {
                  const schema = PARAM_SCHEMAS[row.distribution];
                  return (
                    <div
                      key={row.id}
                      data-testid={`mc-wizard-variable-${row.id}`}
                      style={{
                        padding: 12, borderRadius: 8, marginBottom: 8,
                        border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <input
                          type="text"
                          data-testid={`mc-wizard-variable-${row.id}-name`}
                          value={row.name}
                          onChange={(e) => updateVariableField(row.id, { name: e.target.value })}
                          placeholder="variable_name"
                          style={{
                            flex: 1, padding: '6px 10px', borderRadius: 6,
                            border: '1px solid var(--border)', background: 'var(--bg-primary)',
                            color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace',
                          }}
                        />
                        <select
                          data-testid={`mc-wizard-variable-${row.id}-distribution`}
                          value={row.distribution}
                          onChange={(e) => updateVariableDistribution(row.id, e.target.value as DistributionType)}
                          style={{
                            padding: '6px 10px', borderRadius: 6,
                            border: '1px solid var(--border)', background: 'var(--bg-primary)',
                            color: 'var(--text-primary)', fontSize: 13,
                          }}
                        >
                          {ALL_DISTRIBUTIONS.map((d) => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          data-testid={`mc-wizard-variable-${row.id}-remove`}
                          onClick={() => removeVariable(row.id)}
                          disabled={variables.length === 1}
                          title={variables.length === 1 ? 'At least one variable required' : 'Remove variable'}
                          style={{
                            padding: '6px 10px', borderRadius: 6,
                            border: '1px solid var(--border)', background: 'var(--bg-primary)',
                            color: variables.length === 1 ? 'var(--text-secondary)' : '#dc2626',
                            fontSize: 13, cursor: variables.length === 1 ? 'not-allowed' : 'pointer',
                            opacity: variables.length === 1 ? 0.4 : 1,
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <div
                        data-testid={`mc-wizard-variable-${row.id}-params`}
                        style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                      >
                        {schema.map((field) => (
                          <div key={field.name} style={{ flex: 1, minWidth: 110 }}>
                            <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>
                              {field.label}
                            </label>
                            <input
                              type="number"
                              step="any"
                              data-testid={`mc-wizard-variable-${row.id}-param-${field.name}`}
                              value={row.params[field.name] ?? ''}
                              onChange={(e) => updateVariableParam(row.id, field.name, e.target.value)}
                              style={{
                                width: '100%', padding: '4px 8px', borderRadius: 4,
                                border: '1px solid var(--border)', background: 'var(--bg-primary)',
                                color: 'var(--text-primary)', fontSize: 12,
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      {!isVariableValid(row) && row.name.length > 0 && (
                        <div data-testid={`mc-wizard-variable-${row.id}-error`} style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                          Invalid parameters for {row.distribution} distribution.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                data-testid="mc-wizard-add-variable"
                onClick={addVariable}
                style={{
                  marginTop: 8, padding: '6px 12px', borderRadius: 6,
                  border: '1px dashed var(--border)', background: 'transparent',
                  color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', width: '100%',
                }}
              >
                + Add variable
              </button>
              {!variableNamesUnique && (
                <div data-testid="mc-wizard-variable-names-unique-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 8 }}>
                  Variable names must be unique.
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="mc-wizard-step-3">
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Number of trials *
                </label>
                <input
                  type="number"
                  data-testid="mc-wizard-num-trials"
                  min={100}
                  max={100000}
                  step={100}
                  value={numTrials}
                  onChange={(e) => setNumTrials(parseInt(e.target.value) || 0)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
                {(numTrials < 100 || numTrials > 100000) && (
                  <div data-testid="mc-wizard-num-trials-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                    Number of trials must be between 100 and 100,000.
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  RNG seed (optional)
                </label>
                <input
                  type="text"
                  data-testid="mc-wizard-seed"
                  value={seedRaw}
                  onChange={(e) => setSeedRaw(e.target.value)}
                  placeholder="Leave empty for non-deterministic"
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 6,
                    border: '1px solid var(--border)', background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace',
                  }}
                />
                {!seedValid && (
                  <div data-testid="mc-wizard-seed-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                    Seed must be an integer (or empty).
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="mc-wizard-step-4">
              <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                Define a Python expression over your input variables.
                Available variables: {variableNames.length > 0 ? variableNames.map((n) => <code key={n} style={{ marginRight: 6 }}>{n}</code>) : '(none defined)'}
              </div>
              <textarea
                data-testid="mc-wizard-target-expression"
                value={targetExpression}
                onChange={(e) => setTargetExpression(e.target.value)}
                rows={4}
                placeholder="revenue - cost"
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'var(--bg-primary)',
                  color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace',
                }}
              />
            </div>
          )}

          {step === 5 && (
            <div data-testid="mc-wizard-step-5">
              <div style={{
                padding: 16, borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-secondary)', fontSize: 13, lineHeight: 1.6,
              }}>
                <div data-testid="mc-wizard-review-summary">
                  Run Monte Carlo simulation <strong>{name}</strong>
                  {description ? ` (${description})` : ''}.
                  <br />
                  Variables: <strong>{variables.length}</strong> ({variableNames.join(', ')}).
                  <br />
                  Trials: <strong>{numTrials.toLocaleString()}</strong>.
                  <br />
                  Seed: <strong>{seedRaw.trim() === '' ? 'random' : seedRaw.trim()}</strong>.
                  <br />
                  Target: <code>{targetExpression}</code>.
                </div>
              </div>
            </div>
          )}
      </>
    </WizardModalShell>
  );
};

export default MonteCarloWizard;
